import { API_BASE_URL } from './config';
import {
  AvailabilitySlot,
  AvailabilityPolicy,
  CalendarConnection,
  EnrollmentIntent,
  Instrument,
  StripeCustomerSummary,
  StripePrice,
  StripeProduct,
  TeacherConfig,
} from '../types/ownerConsole';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

async function get<T>(path: string): Promise<T> {
  const token = localStorage.getItem('auth_token');
  const response = await fetch(`${API_BASE_URL}/api/v1${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  const payload = (await response.json()) as ApiResponse<T>;
  if (!payload.success) {
    throw new Error(payload.message || 'Request failed');
  }
  return payload.data;
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  return requestWithBody<T>('POST', path, body);
}

async function put<T>(path: string, body?: unknown): Promise<T> {
  return requestWithBody<T>('PUT', path, body);
}

async function requestWithBody<T>(method: 'POST' | 'PUT', path: string, body?: unknown): Promise<T> {
  const token = localStorage.getItem('auth_token');
  const response = await fetch(`${API_BASE_URL}/api/v1${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  const payload = (await response.json()) as ApiResponse<T>;
  if (!payload.success) {
    throw new Error(payload.message || 'Request failed');
  }
  return payload.data;
}

export const ownerConsoleService = {
  async siteConfig(): Promise<{ teacher: TeacherConfig; instruments: Instrument[] }> {
    return get('/admin/site-config');
  },

  async updateSiteConfig(config: { teacher: TeacherConfig; instruments: Instrument[] }): Promise<{ teacher: TeacherConfig; instruments: Instrument[] }> {
    return put('/admin/site-config', config);
  },

  async products(): Promise<StripeProduct[]> {
    return get('/admin/stripe/products');
  },

  async prices(): Promise<StripePrice[]> {
    return get('/admin/stripe/prices');
  },

  async customers(): Promise<StripeCustomerSummary[]> {
    return get('/admin/stripe/customers');
  },

  async calendarStatus(): Promise<CalendarConnection> {
    return get('/calendar/status');
  },

  async availabilityPolicy(): Promise<AvailabilityPolicy> {
    return get('/admin/availability-policy');
  },

  async updateAvailabilityPolicy(policy: AvailabilityPolicy): Promise<AvailabilityPolicy> {
    return put('/admin/availability-policy', policy);
  },

  async disconnectGoogleCalendar(): Promise<CalendarConnection> {
    return post('/admin/google/disconnect');
  },

  async availability(params?: { start?: string; days?: number; duration_minutes?: number }): Promise<AvailabilitySlot[]> {
    const query = new URLSearchParams();
    if (params?.start) query.set('start', params.start);
    if (params?.days) query.set('days', String(params.days));
    if (params?.duration_minutes) query.set('duration_minutes', String(params.duration_minutes));
    return get(`/calendar/availability${query.toString() ? `?${query.toString()}` : ''}`);
  },

  async enrollmentIntents(): Promise<EnrollmentIntent[]> {
    return get('/admin/enrollments');
  },

  async createManualEnrollment(payload: {
    instrument_id: string;
    status: string;
    student_name?: string | null;
    parent_name?: string | null;
    contact_email?: string | null;
    contact_phone?: string | null;
    lesson_time?: string | null;
    notes?: string | null;
    stripe_customer_id?: string | null;
    stripe_checkout_session_id?: string | null;
    nylas_event_id?: string | null;
  }): Promise<EnrollmentIntent> {
    return post('/admin/enrollments', payload);
  },

  googleAuthorizeUrl(): string {
    return `${API_BASE_URL}/api/v1/admin/google/authorize`;
  },
};
